import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  Search,
  Dumbbell,
  X,
  GripVertical,
  PlusCircle,
} from 'lucide-react';
import { programsApi, exercisesApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { NumberStepper } from '@/components/NumberStepper';
import { generateId } from '@/lib/utils';
import type { Exercise, ProgramExercise, ProgramExerciseSet, MuscleGroup } from '@/types';
import { MUSCLE_GROUPS } from '@/types';

// Paikallinen tila pohjan muokkausta varten
interface LocalTemplateExercise {
  tempId: string;
  exerciseId: string;
  exerciseName: string;
  orderIndex: number;
  notes: string | null;
  sets: LocalTemplateSet[];
}

interface LocalTemplateSet {
  tempId: string;
  setNumber: number;
  targetReps: number | null;
  notes: string | null;
}

export function TemplateFormPage() {
  const { programId, templateId } = useParams<{ programId: string; templateId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Lomakkeen tila
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [exercises, setExercises] = useState<LocalTemplateExercise[]>([]);
  const [showExerciseModal, setShowExerciseModal] = useState(false);
  const [exerciseSearch, setExerciseSearch] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  // Uuden liikkeen luonti
  const [showCreateExercise, setShowCreateExercise] = useState(false);
  const [newExerciseName, setNewExerciseName] = useState('');
  const [newExerciseMuscleGroup, setNewExerciseMuscleGroup] = useState<MuscleGroup | ''>('');

  // Hae pohja
  const { data: template, isLoading: loadingTemplate } = useQuery({
    queryKey: ['template', programId, templateId],
    queryFn: () => programsApi.getTemplate(programId!, templateId!),
    enabled: !!programId && !!templateId,
  });

  // Hae ohjelma (navigaatiota varten)
  const { data: program } = useQuery({
    queryKey: ['program', programId],
    queryFn: () => programsApi.get(programId!),
    enabled: !!programId,
  });

  // Hae liikkeet
  const { data: exercisesData } = useQuery({
    queryKey: ['exercises'],
    queryFn: () => exercisesApi.list(),
  });

  // Lataa pohjan tiedot lomakkeeseen
  useEffect(() => {
    if (template) {
      setName(template.name);
      setNotes(template.notes || '');

      const localExercises: LocalTemplateExercise[] = (template.exercises || []).map(
        (pe: ProgramExercise) => ({
          tempId: pe.id,
          exerciseId: pe.exerciseId,
          exerciseName: pe.exercise?.name || 'Tuntematon',
          orderIndex: pe.orderIndex,
          notes: pe.notes,
          sets: (pe.sets || []).map((set: ProgramExerciseSet) => ({
            tempId: set.id,
            setNumber: set.setNumber,
            targetReps: set.targetReps,
            notes: set.notes,
          })),
        })
      );

      setExercises(localExercises);
      setHasChanges(false);
    }
  }, [template]);

  // Päivitä pohja
  const updateTemplateMutation = useMutation({
    mutationFn: async () => {
      // Päivitä pohjan perustiedot
      await programsApi.updateTemplate(programId!, templateId!, {
        name: name.trim(),
        notes: notes.trim() || undefined,
      });

      // Poista vanhat liikkeet ja lisää uudet
      if (template?.exercises) {
        for (const pe of template.exercises) {
          await programsApi.removeExercise(programId!, templateId!, pe.id);
        }
      }

      // Lisää liikkeet ja sarjat
      for (let i = 0; i < exercises.length; i++) {
        const exercise = exercises[i]!;
        const addedExercise = await programsApi.addExercise(programId!, templateId!, {
          exerciseId: exercise.exerciseId,
          orderIndex: i,
          notes: exercise.notes || undefined,
          sets: exercise.sets.map((set, setIndex) => ({
            setNumber: setIndex + 1,
            targetReps: set.targetReps ?? undefined,
            notes: set.notes ?? undefined,
          })),
        });

        // API:ssa sarjat lisätään jo addExercise-kutsussa, ei tarvitse erikseen
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template', programId, templateId] });
      queryClient.invalidateQueries({ queryKey: ['program', programId] });
      setHasChanges(false);
      navigate(`/programs/${programId}`);
    },
  });

  // Luo uusi liike
  const createExerciseMutation = useMutation({
    mutationFn: (data: { name: string; muscleGroup?: MuscleGroup }) =>
      exercisesApi.create(data),
    onSuccess: (newExercise) => {
      queryClient.invalidateQueries({ queryKey: ['exercises'] });
      // Lisää uusi liike suoraan pohjaan
      addExercise(newExercise);
      // Nollaa lomake
      setShowCreateExercise(false);
      setNewExerciseName('');
      setNewExerciseMuscleGroup('');
    },
  });

  const handleCreateExercise = () => {
    if (newExerciseName.trim()) {
      createExerciseMutation.mutate({
        name: newExerciseName.trim(),
        muscleGroup: newExerciseMuscleGroup || undefined,
      });
    }
  };

  // Lisää liike
  const addExercise = (exercise: Exercise) => {
    const newExercise: LocalTemplateExercise = {
      tempId: generateId(),
      exerciseId: exercise.id,
      exerciseName: exercise.name,
      orderIndex: exercises.length,
      notes: null,
      sets: [
        {
          tempId: generateId(),
          setNumber: 1,
          targetReps: 10,
          notes: null,
        },
      ],
    };

    setExercises([...exercises, newExercise]);
    setShowExerciseModal(false);
    setExerciseSearch('');
    setHasChanges(true);
  };

  // Poista liike
  const removeExercise = (tempId: string) => {
    setExercises(exercises.filter((e) => e.tempId !== tempId));
    setHasChanges(true);
  };

  // Lisää sarja liikkeeseen
  const addSet = (exerciseTempId: string) => {
    setExercises(
      exercises.map((e) => {
        if (e.tempId === exerciseTempId) {
          const lastSet = e.sets[e.sets.length - 1];
          return {
            ...e,
            sets: [
              ...e.sets,
              {
                tempId: generateId(),
                setNumber: e.sets.length + 1,
                targetReps: lastSet?.targetReps ?? 10,
                notes: null,
              },
            ],
          };
        }
        return e;
      })
    );
    setHasChanges(true);
  };

  // Poista sarja
  const removeSet = (exerciseTempId: string, setTempId: string) => {
    setExercises(
      exercises.map((e) => {
        if (e.tempId === exerciseTempId) {
          return {
            ...e,
            sets: e.sets.filter((s) => s.tempId !== setTempId),
          };
        }
        return e;
      })
    );
    setHasChanges(true);
  };

  // Päivitä sarjan toistot
  const updateSetReps = (exerciseTempId: string, setTempId: string, reps: number | null) => {
    setExercises(
      exercises.map((e) => {
        if (e.tempId === exerciseTempId) {
          return {
            ...e,
            sets: e.sets.map((s) =>
              s.tempId === setTempId ? { ...s, targetReps: reps } : s
            ),
          };
        }
        return e;
      })
    );
    setHasChanges(true);
  };

  // Suodata liikkeet
  const filteredExercises =
    exercisesData?.exercises.filter((e) =>
      e.name.toLowerCase().includes(exerciseSearch.toLowerCase())
    ) || [];

  if (loadingTemplate) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">Treenipohjaa ei löytynyt</p>
        <Link to={`/programs/${programId}`} className="btn btn-secondary mt-4">
          Takaisin ohjelmaan
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Navigaatio */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => {
            if (hasChanges) {
              if (window.confirm('Sinulla on tallentamattomia muutoksia. Haluatko poistua?')) {
                navigate(`/programs/${programId}`);
              }
            } else {
              navigate(`/programs/${programId}`);
            }
          }}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <p className="text-sm text-gray-500">{program?.name}</p>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Muokkaa treenipohjaa
          </h1>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          updateTemplateMutation.mutate();
        }}
        className="space-y-6"
      >
        {/* Perustiedot */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 dark:text-white mb-4">Perustiedot</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="template-name" className="label">
                Pohjan nimi *
              </label>
              <input
                type="text"
                id="template-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setHasChanges(true);
                }}
                className="input w-full"
                placeholder="esim. Päivä A - Push"
                required
              />
            </div>
            <div>
              <label htmlFor="template-notes" className="label">
                Muistiinpanot
              </label>
              <textarea
                id="template-notes"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setHasChanges(true);
                }}
                rows={2}
                className="input w-full resize-none"
                placeholder="Lisätietoja pohjasta..."
              />
            </div>
          </div>
        </div>

        {/* Liikkeet */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Dumbbell className="h-5 w-5 text-primary-500" />
              Liikkeet ({exercises.length})
            </h2>
            <button
              type="button"
              onClick={() => setShowExerciseModal(true)}
              className="btn btn-secondary"
            >
              <Plus className="h-4 w-4" />
              Lisää liike
            </button>
          </div>

          {exercises.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Ei vielä liikkeitä</p>
              <button
                type="button"
                onClick={() => setShowExerciseModal(true)}
                className="text-primary-600 hover:underline mt-2"
              >
                Lisää ensimmäinen liike
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {exercises.map((exercise, exerciseIndex) => (
                <div
                  key={exercise.tempId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                >
                  {/* Liikkeen otsikko */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-500">#{exerciseIndex + 1}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {exercise.exerciseName}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm(`Poistetaanko liike "${exercise.exerciseName}"?`)) {
                          removeExercise(exercise.tempId);
                        }
                      }}
                      className="p-1.5 text-gray-400 hover:text-red-500"
                      title="Poista liike"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Sarjat */}
                  <div className="space-y-2">
                    {exercise.sets.map((set, setIndex) => (
                      <div
                        key={set.tempId}
                        className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg"
                      >
                        <span className="text-sm font-medium text-gray-500 w-16">
                          Sarja {setIndex + 1}
                        </span>
                        <div className="flex-1">
                          <NumberStepper
                            value={set.targetReps}
                            onChange={(val) =>
                              updateSetReps(exercise.tempId, set.tempId, val)
                            }
                            min={1}
                            max={100}
                            step={1}
                            label="Tavoite"
                            unit="toistoa"
                            presets={[5, 6, 8, 10, 12, 15, 20]}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (exercise.sets.length > 1) {
                              removeSet(exercise.tempId, set.tempId);
                            }
                          }}
                          disabled={exercise.sets.length <= 1}
                          className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Poista sarja"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Lisää sarja */}
                  <button
                    type="button"
                    onClick={() => addSet(exercise.tempId)}
                    className="mt-2 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <Plus className="h-4 w-4" />
                    Lisää sarja
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Toimintopainikkeet */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => {
              if (hasChanges) {
                if (window.confirm('Sinulla on tallentamattomia muutoksia. Haluatko poistua?')) {
                  navigate(`/programs/${programId}`);
                }
              } else {
                navigate(`/programs/${programId}`);
              }
            }}
            className="btn btn-secondary flex-1"
          >
            Peruuta
          </button>
          <button
            type="submit"
            disabled={!name.trim() || updateTemplateMutation.isPending}
            className="btn btn-primary flex-1"
          >
            {updateTemplateMutation.isPending ? (
              <LoadingSpinner size="sm" className="text-white" />
            ) : (
              <>
                <Save className="h-4 w-4" />
                Tallenna pohja
              </>
            )}
          </button>
        </div>
      </form>

      {/* Liikevalikko */}
      {showExerciseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 w-full sm:max-w-md sm:rounded-xl max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {showCreateExercise ? 'Luo uusi liike' : 'Valitse liike'}
                </h3>
                <button
                  onClick={() => {
                    setShowExerciseModal(false);
                    setShowCreateExercise(false);
                    setExerciseSearch('');
                    setNewExerciseName('');
                    setNewExerciseMuscleGroup('');
                  }}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {showCreateExercise ? (
                // Uuden liikkeen luontilomake
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Liikkeen nimi *
                    </label>
                    <input
                      type="text"
                      value={newExerciseName}
                      onChange={(e) => setNewExerciseName(e.target.value)}
                      placeholder="esim. Bulgarian split squat"
                      className="input w-full"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Lihasryhmä (valinnainen)
                    </label>
                    <select
                      value={newExerciseMuscleGroup}
                      onChange={(e) => setNewExerciseMuscleGroup(e.target.value as MuscleGroup | '')}
                      className="input w-full"
                    >
                      <option value="">Valitse lihasryhmä...</option>
                      {MUSCLE_GROUPS.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.emoji} {group.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateExercise(false);
                        setNewExerciseName('');
                        setNewExerciseMuscleGroup('');
                      }}
                      className="btn btn-secondary flex-1"
                    >
                      Takaisin
                    </button>
                    <button
                      type="button"
                      onClick={handleCreateExercise}
                      disabled={!newExerciseName.trim() || createExerciseMutation.isPending}
                      className="btn btn-primary flex-1"
                    >
                      {createExerciseMutation.isPending ? (
                        <LoadingSpinner size="sm" className="text-white" />
                      ) : (
                        <>
                          <Plus className="h-4 w-4" />
                          Luo liike
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                // Hakukenttä
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={exerciseSearch}
                    onChange={(e) => setExerciseSearch(e.target.value)}
                    placeholder="Hae liikettä..."
                    className="input pl-10 w-full"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {!showCreateExercise && (
              <div className="flex-1 overflow-y-auto p-2">
                {/* Luo uusi liike -nappi */}
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateExercise(true);
                    setNewExerciseName(exerciseSearch);
                  }}
                  className="w-full text-left px-3 py-2 mb-2 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg border border-primary-200 dark:border-primary-800"
                >
                  <div className="flex items-center gap-2 text-primary-700 dark:text-primary-300">
                    <PlusCircle className="h-5 w-5" />
                    <span className="font-medium">
                      {exerciseSearch ? `Luo "${exerciseSearch}"` : 'Luo uusi liike'}
                    </span>
                  </div>
                  <p className="text-sm text-primary-600 dark:text-primary-400 ml-7">
                    Lisää oma liike, jos sopivaa ei löydy
                  </p>
                </button>

                {filteredExercises.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">
                    Ei tuloksia haulle "{exerciseSearch}"
                  </p>
                ) : (
                  <div className="space-y-1">
                    {filteredExercises.map((exercise) => (
                      <button
                        key={exercise.id}
                        type="button"
                        onClick={() => addExercise(exercise)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        <div className="font-medium text-gray-900 dark:text-white">
                          {exercise.name}
                        </div>
                        {exercise.muscleGroup && (
                          <div className="text-sm text-gray-500">{exercise.muscleGroup}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
